import type { ReactNode } from 'react';
import { FOCUS_RING, TONE, priorityTone, type Tone } from './tone';

// Phase 36 §21a — chips, selection controls and avatars.
//
// Every chip is wash + border + ink together, so each one survives a greyscale
// check: hue alone is not a signal for the eight percent of readers who cannot
// separate the terracotta from the green.

const MONO = 'co-mono';

const CHIP =
  `${MONO} inline-flex items-center gap-[5px] whitespace-nowrap rounded-[5px] border ` +
  'px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em]';

/** The one chip. Everything with a state uses it. */
export function StatusChip({
  tone = 'neutral',
  children,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  title?: string;
}) {
  const t = TONE[tone];
  return (
    <span
      className={CHIP}
      style={{ borderColor: t.line, background: t.wash, color: t.ink }}
      title={title}
    >
      {children}
    </span>
  );
}

/**
 * Priority, where only the top two carry hue.
 *
 * Four coloured priorities is four signals, which is none: if `low` is also a
 * colour then colour stops meaning "look at this" and starts meaning "this row
 * has a priority", which every row does.
 */
export function PriorityChip({ priority }: { priority: string | null | undefined }) {
  if (!priority) return null;
  return <StatusChip tone={priorityTone(priority)}>{priority}</StatusChip>;
}

/**
 * An applied filter, carrying the field it filters on.
 *
 * The field name lives inside the chip so a row of them reads as a sentence.
 * Without it, `Quote` and `Urgent` sit side by side with no way to tell which
 * column either one came from.
 */
export function FilterChip({
  field,
  value,
  onRemove,
}: {
  field: string;
  value: ReactNode;
  onRemove?: () => void;
}) {
  return (
    <span
      className="inline-flex items-center gap-[7px] rounded-md border px-[9px] py-[5px] text-[12px] text-[var(--co-ink)]"
      style={{ borderColor: 'var(--co-brass-line)', background: 'var(--co-brass-wash)' }}
    >
      <span
        className={`${MONO} text-[9px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)]`}
      >
        {field}
      </span>
      {value}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${field} filter`}
          className={`text-[var(--co-ink-3)] hover:text-[var(--co-ink)] ${FOCUS_RING}`}
        >
          ✕
        </button>
      )}
    </span>
  );
}

// -------------------------------------------------------------- selection

/**
 * Rest, checked, and the third state that gets skipped.
 *
 * `indeterminate` is what a header checkbox shows when some of the page is
 * ticked. Without it, three of forty selected renders as an empty box and the
 * next click looks like "select all" when the control is actually toggling.
 */
export function Checkbox({
  checked = false,
  indeterminate = false,
  onChange,
  label,
  disabled,
}: {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (next: boolean) => void;
  /** Required for the screen reader; visually hidden when the row carries it. */
  label: string;
  disabled?: boolean;
}) {
  const on = checked || indeterminate;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`flex h-[14px] w-[14px] flex-none items-center justify-center rounded-[3px] border transition-colors disabled:opacity-50 ${FOCUS_RING}`}
      style={{
        borderColor: on ? 'var(--co-brass-ink)' : 'var(--co-line-strong)',
        background: on ? 'var(--co-brass-ink)' : 'var(--co-raised)',
      }}
    >
      {indeterminate ? (
        <span
          className="h-[1.5px] w-[7px]"
          style={{ background: 'var(--co-brass-on)' }}
          aria-hidden="true"
        />
      ) : checked ? (
        <span className="text-[9px] leading-none text-[var(--co-brass-on)]" aria-hidden="true">
          ✓
        </span>
      ) : null}
    </button>
  );
}

export function Radio({
  checked = false,
  onChange,
  label,
  disabled,
}: {
  checked?: boolean;
  onChange?: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`flex h-[13px] w-[13px] flex-none items-center justify-center rounded-full border transition-colors disabled:opacity-50 ${FOCUS_RING}`}
      style={{ borderColor: checked ? 'var(--co-brass-ink)' : 'var(--co-line-strong)' }}
    >
      {checked && (
        <span
          className="h-[6px] w-[6px] rounded-full"
          style={{ background: 'var(--co-brass-ink)' }}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

/**
 * The only pill in the system.
 *
 * On is `--co-green` because it means ACTIVE, not because it means good. A
 * toggle that switches something off is not a failure and must not go red.
 */
export function Toggle({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`flex h-[19px] w-[34px] flex-none items-center rounded-[10px] p-[2px] transition-colors disabled:opacity-50 ${FOCUS_RING}`}
      style={{ background: on ? 'var(--co-green)' : 'var(--co-line-strong)' }}
    >
      <span
        className="h-[15px] w-[15px] rounded-full transition-transform"
        style={{
          background: 'var(--co-raised)',
          transform: on ? 'translateX(15px)' : 'translateX(0)',
        }}
        aria-hidden="true"
      />
    </button>
  );
}

export interface SegmentOption<T extends string> {
  id: T;
  label: string;
}

/** Two to four options. Beyond that it is a select. */
export function Segmented<T extends string>({
  options,
  active,
  onSelect,
  label,
}: {
  options: readonly SegmentOption<T>[];
  active: T;
  onSelect: (id: T) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="inline-flex items-center gap-[6px] rounded-lg bg-[var(--co-selected)] p-1"
    >
      {options.map((option) => {
        const on = option.id === active;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onSelect(option.id)}
            className={`${MONO} rounded-md px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em] transition-colors ${FOCUS_RING}`}
            style={
              on
                ? {
                    background: 'var(--co-raised)',
                    color: 'var(--co-ink)',
                    fontWeight: 600,
                    boxShadow: 'var(--co-shadow)',
                  }
                : { color: 'var(--co-ink-3)', fontWeight: 500 }
            }
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Square, never circular, and never randomly coloured.
 *
 * The grounds are drawn from a fixed set of five deliberately desaturated
 * tones chosen so the initials clear 4.5:1 on every one — initials are content,
 * not decoration. A hash into full-saturation hues puts a sixth chroma on the
 * screen and lands some names on unreadable pairs.
 */
const AVATAR_GROUND = ['#6E5F45', '#57536E', '#4C5D54', '#714B4B', '#5A5347'] as const;

export function Avatar({ name, size = 28 }: { name: string | null | undefined; size?: number }) {
  const trimmed = (name ?? '').trim();
  if (!trimmed) {
    return (
      <span
        className="flex flex-none items-center justify-center rounded-[7px] border border-dashed border-[var(--co-line-strong)] text-[11px] text-[var(--co-ink-4)]"
        style={{ width: size, height: size }}
        title="Unassigned"
        aria-label="Unassigned"
      >
        ?
      </span>
    );
  }
  let hash = 0;
  for (let i = 0; i < trimmed.length; i += 1) hash = (hash * 31 + trimmed.charCodeAt(i)) >>> 0;
  return (
    <span
      className="flex flex-none items-center justify-center rounded-[7px] font-semibold"
      style={{
        width: size,
        height: size,
        background: AVATAR_GROUND[hash % AVATAR_GROUND.length],
        color: '#EFE8DC',
        fontSize: Math.round(size * 0.39),
      }}
      title={trimmed}
    >
      {trimmed.charAt(0).toUpperCase()}
    </span>
  );
}
